import * as Dialog from '@radix-ui/react-dialog';
import styled, { keyframes } from 'styled-components';
import { useSelector } from 'react-redux';
import close_gray from '../../../icons/close_blue.svg';
import briefcase from '../../../icons/briefcase.svg';
import banIcon from '../../../icons/ban.svg';
import useGetProhibitedOperations from '../../../hooks/useGetProhibitedOperations';
import useGetIndustry from '../../../hooks/useGetIndustry';
import { PAGES } from '../../../constants';
import EditSummaryButton from '../EditSummaryButton';
import { format, parseISO } from 'date-fns';
import { formatCurrency } from '../../../helpers/formatNumber';
import { selectPolicyTypes } from '../../../selectors/application';
import { PolicyType } from '../../../types/enums';

type SummaryDrawerProps = {
  application: Application;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export default function BusinessSummaryDrawer({ application, open, onOpenChange }: SummaryDrawerProps) {
  const { data: prohibitedOperations } = useGetProhibitedOperations(application.akHash);
  const { data: fetchedIndustryName } = useGetIndustry(!application.industryName ? application.akHash : undefined);
  const displayIndustryName = application.industryName || fetchedIndustryName;
  const policyTypes = useSelector(selectPolicyTypes);
  const isPlOnly = policyTypes.length === 1 && policyTypes.includes(PolicyType.PL);
  const hasProhibitedOperations = prohibitedOperations && prohibitedOperations.length > 0;

  const closeDrawer = () => onOpenChange(false);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <StyledOverlay />
        <StyledContent data-cy="business-summary-drawer">
          <Dialog.Close asChild>
            <CloseButton>
              <img src={close_gray} alt="Close" height={16} width={12} />
            </CloseButton>
          </Dialog.Close>
          <DrawerHeader>
            <img src={briefcase} alt="Briefcase" height={24} width={24} />
            <Dialog.Title asChild>
              <DrawerTitle>Your Business</DrawerTitle>
            </Dialog.Title>
          </DrawerHeader>
          <DrawerSubtitle>Review and edit any section to update your business details.</DrawerSubtitle>
          <DrawerContent>
            {displayIndustryName && (
              <BusinessSummarySection>
                <SectionTitleRow>
                  <SectionTitle>Industry</SectionTitle>
                  <EditSummaryButton page={PAGES.INDUSTRY} onEditClick={closeDrawer} label="industry">
                    Edit Info
                  </EditSummaryButton>
                </SectionTitleRow>
                <SectionSubtitle>{displayIndustryName}</SectionSubtitle>
                {hasProhibitedOperations && !isPlOnly && (
                  <ProhibitedContainer>
                    <ProhibitedHeading>
                      <img src={banIcon} alt="Warning" />
                      Ineligible Activities and Exposures
                    </ProhibitedHeading>
                    <ProhibitedDescriptionRow>
                      <ProhibitedInfo>
                        The specific activities listed below would make this business ineligible for a quote.
                      </ProhibitedInfo>
                    </ProhibitedDescriptionRow>
                    <ProhibitedList>
                      {prohibitedOperations.map((operation: string, index: number) => (
                        <li key={index}>{operation}</li>
                      ))}
                    </ProhibitedList>
                    <ProhibitedInfoBold>
                      If your business performs any of these activities, do not proceed with this quote.
                    </ProhibitedInfoBold>
                  </ProhibitedContainer>
                )}
              </BusinessSummarySection>
            )}
            <BusinessSummarySection>
              <SectionTitleRow>
                <SectionTitle>Business Information</SectionTitle>
                <EditSummaryButton
                  page={PAGES.BUSINESS_INFORMATION}
                  onEditClick={closeDrawer}
                  label="business-information"
                >
                  Edit Info
                </EditSummaryButton>
              </SectionTitleRow>
              <FieldGroup>
                <FieldRow>
                  <FieldLabel>Business Name</FieldLabel>
                  <FieldValue>{application.legalBusinessName}</FieldValue>
                  {application.dba && <FieldValue>{application.dba}</FieldValue>}
                </FieldRow>
                <FieldRow>
                  <FieldLabel>Number of Employees</FieldLabel>
                  <FieldValue>{application.numEmployees}</FieldValue>
                </FieldRow>
                <FieldRow>
                  <FieldLabel>Business Start Date</FieldLabel>
                  <FieldValue>
                    {application.businessStartDate ? format(parseISO(application.businessStartDate), 'MM/yyyy') : ''}
                  </FieldValue>
                </FieldRow>
                <FieldRow>
                  <FieldLabel>Claims History?</FieldLabel>
                  <FieldValue>{application.claimsThreeYears ? 'Yes' : 'No'}</FieldValue>
                </FieldRow>
              </FieldGroup>
            </BusinessSummarySection>
            <BusinessSummarySection>
              <SectionTitleRow>
                <SectionTitle>Finance Basics</SectionTitle>
                <EditSummaryButton page={PAGES.FINANCE_BASICS} onEditClick={closeDrawer} label="finance-basics">
                  Edit Info
                </EditSummaryButton>
              </SectionTitleRow>
              <FieldGroup>
                <FieldRow>
                  <FieldLabel>Estimated Revenue</FieldLabel>
                  <FieldValue>{formatCurrency(application.grossAnnualSales)}</FieldValue>
                </FieldRow>
                <FieldRow>
                  <FieldLabel>Estimated Annual Payroll</FieldLabel>
                  <FieldValue>{formatCurrency(application.annualPayroll)}</FieldValue>
                </FieldRow>
              </FieldGroup>
            </BusinessSummarySection>
            <BusinessSummarySection>
              <SectionTitleRow>
                <SectionTitle>Location Info</SectionTitle>
                <EditSummaryButton page={PAGES.LOCATIONS} onEditClick={closeDrawer} label="locations">
                  Edit Info
                </EditSummaryButton>
              </SectionTitleRow>
              <FieldGroup>
                <FieldRow>
                  <FieldLabel>Business Location</FieldLabel>
                  <FieldValue>
                    {application.locations?.[0]
                      ? `${application.locations[0].street} ${application.locations[0].city}, ${application.locations[0].state} ${application.locations[0].zip}`
                      : ''}
                  </FieldValue>
                </FieldRow>
                <FieldRow>
                  <FieldLabel>Premises Type</FieldLabel>
                  <FieldValue>{application.locations?.[0]?.locationType?.replace(/([A-Z])/g, ' $1').trim()}</FieldValue>
                </FieldRow>
                <FieldRow>
                  <FieldLabel>Sprinkler System</FieldLabel>
                  <FieldValue>{application.locations?.[0]?.hasSprinklers ? 'Yes' : 'No'}</FieldValue>
                </FieldRow>
              </FieldGroup>
            </BusinessSummarySection>
            <BusinessSummarySection>
              <SectionTitleRow>
                <SectionTitle>Contact Info</SectionTitle>
                <EditSummaryButton
                  page={PAGES.CONTACT_INFORMATION}
                  onEditClick={closeDrawer}
                  label="contact-information"
                >
                  Edit Info
                </EditSummaryButton>
              </SectionTitleRow>
              <FieldGroup>
                <FieldRow>
                  <FieldLabel>First Name</FieldLabel>
                  <FieldValue>{application.contactFirstName}</FieldValue>
                </FieldRow>
                <FieldRow>
                  <FieldLabel>Last Name</FieldLabel>
                  <FieldValue>{application.contactLastName}</FieldValue>
                </FieldRow>
                <FieldRow>
                  <FieldLabel>Email</FieldLabel>
                  <FieldValue>{application.contactEmail}</FieldValue>
                </FieldRow>
                <FieldRow>
                  <FieldLabel>Mailing Address</FieldLabel>
                  <FieldValue>
                    {application.mailingAddressStreet
                      ? `${application.mailingAddressStreet} ${application.mailingAddressCity}, ${application.mailingAddressState} ${application.mailingAddressZip}`
                      : ''}
                  </FieldValue>
                </FieldRow>
              </FieldGroup>
            </BusinessSummarySection>
          </DrawerContent>
        </StyledContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const slideIn = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const StyledOverlay = styled(Dialog.Overlay)`
  background: rgba(0, 0, 0, 0.35);
  position: fixed;
  inset: 0;
  z-index: 99;

  &[data-state='open'] {
    animation: ${fadeIn} 0.4s forwards;
  }
  &[data-state='closed'] {
    animation: ${fadeOut} 0.4s forwards;
  }
`;

const StyledContent = styled(Dialog.Content)`
  position: fixed;
  top: 0;
  right: 0;
  max-width: 520px;
  height: 100%;
  padding: 56px 40px 24px 40px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  background: ${({ theme }) => theme.colors.neutral.white};
  box-shadow: 0 0 10px 1px rgba(0, 0, 0, 0.1);
  z-index: 100;
  overflow-y: auto;

  &[data-state='open'] {
    animation: ${slideIn} 0.4s forwards;
  }

  &[data-state='closed'] {
    animation: ${slideOut} 0.4s forwards;
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 768px) {
    padding: 56px 24px 24px 24px;
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const DrawerTitle = styled.h2`
  color: ${({ theme }) => theme.colors.tertiary.zero};
  font-family: ${({ theme }) => theme.font.typeface.primary};
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 28px;
  margin: 0;
`;

const DrawerSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.tertiary.zero};
  font-family: ${({ theme }) => theme.font.typeface.secondary};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  margin: 0;
`;

const DrawerContent = styled.div`
  flex: 1;
  width: 100%;
  padding-bottom: 80px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
`;

const BusinessSummarySection = styled.section`
  padding: 24px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral.light};

  &:last-child {
    border-bottom: none;
  }
`;

const SectionTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SectionTitle = styled.h3`
  color: ${({ theme }) => theme.colors.tertiary.zero};
  font-family: ${({ theme }) => theme.font.typeface.primary};
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 26px;
  margin: 0;
`;

const SectionSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.neutral.dark};
  font-family: ${({ theme }) => theme.font.typeface.secondary};
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  margin: 0;
  padding: 16px 0;
`;

const ProhibitedContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const ProhibitedHeading = styled.h3`
  display: flex;
  padding: 8px;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.pl.ten};
  color: #734d19;
  font-family: ${({ theme }) => theme.font.typeface.secondary};
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  margin: 0 0 8px 0;
`;

const ProhibitedDescriptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 21px;
`;

const ProhibitedInfo = styled.p`
  color: #2f2f31;
  font-family: ${({ theme }) => theme.font.typeface.secondary};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  margin: 0;
`;

const ProhibitedInfoBold = styled(ProhibitedInfo)`
  font-weight: 600;
`;

const ProhibitedList = styled.ul`
  margin: 0;
  padding-left: 16px;
  padding-top: 8px;
  list-style: disc;
  columns: 2;

  li {
    font-family: ${({ theme }) => theme.font.typeface.secondary};
    color: #2f2f31;
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 0;
    margin-left: 21px;
  }

  li::marker {
    font-size: 10px;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  padding-top: 16px;
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
`;

const FieldLabel = styled.span`
  color: ${({ theme }) => theme.colors.neutral.medium};
  font-family: ${({ theme }) => theme.font.typeface.secondary};
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
`;

const FieldValue = styled.span`
  color: #191919;
  font-family: ${({ theme }) => theme.font.typeface.secondary};
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
`;
